import React from 'react';

import { Typography } from '@mui/material';

import { CurrencyFormat } from '@ha/intl';
import { track } from '@hbf/analytics';

import NoListingImage from '@kamernet/assets/no-listing-image.webp';
import { AnalyticsEvent } from '@kamernet/core/Analytics/types';
import {
  Furnishings,
  Listing,
  RoomTypeID,
} from '@kamernet/core/ApiClient/Kamernet';
import { AppServicesContext } from '@kamernet/core/AppServices';
import { AuthContext } from '@kamernet/core/Auth/AuthContext';
import { AuthSource, AuthAction } from '@kamernet/core/Auth/types';
import { useAuthenticationRouting } from '@kamernet/core/Auth/useAuthenticationRouting';
import { reportError } from '@kamernet/core/Errors';
import { useIntl } from '@kamernet/core/Intl';
import { ROUTES } from '@kamernet/core/Routing/routes';
import { CommonStyles } from '@kamernet/styles/CommonStyles';

import {
  RoomTypeIDTranslations,
  FurnishingLabelTranslations,
} from '@kamernet/modules/Listing/translations';
import { ListingChipLabels } from '@kamernet/modules/Listing/types';
import { SearchResultCard } from '@kamernet/modules/Search/components/SearchResultCard';

import { useFormatDateRange } from '../../hooks';
import { handleListingImageError } from '../../utilities';
import { ListingChip } from '../ListingChip';

import styles from './ListingCard.module.css';

export interface ListingCardProps extends Partial<Listing> {
  listingId: number;
  thumbnailUrl: string;
  fullPreviewImageUrl: string;
  isTopAdvert: boolean;
  furnishingId: Furnishings;
  availabilityStartDate: string;
  availabilityEndDate: string | null;
  streetName: string;
  streetSlug: string;
  cityName: string;
  citySlug: string;
  isFavorite: boolean;
  surfaceArea: number;
  listingType: RoomTypeID;
  totalRentalPrice: number;
  utilitiesIncluded: boolean;
  isNewAdvert: boolean;
  isReactForFree: boolean;
  isStudentHouseAdvert: boolean;
}

const ListingCardInternal = ({
  listingId,
  thumbnailUrl,
  fullPreviewImageUrl,
  isTopAdvert,
  furnishingId,
  availabilityStartDate,
  availabilityEndDate,
  streetName,
  streetSlug,
  cityName,
  citySlug,
  isFavorite,
  surfaceArea,
  listingType,
  totalRentalPrice,
  utilitiesIncluded,
  isNewAdvert,
  isReactForFree,
  isStudentHouseAdvert,
}: ListingCardProps) => {
  const { isAuthenticated } = AuthContext.useContext();
  const { T, locale, formatCurrency } = useIntl();
  const { navigateToAuthentication } = useAuthenticationRouting();
  const { apiClient } = AppServicesContext.useContext();
  const formatDateRange = useFormatDateRange();

  const availability = formatDateRange(
    availabilityStartDate,
    availabilityEndDate,
  );

  const listingImageUrl = fullPreviewImageUrl
    ? `${fullPreviewImageUrl}/resize/422-225`
    : thumbnailUrl;

  const listingTypeTranslation = T(RoomTypeIDTranslations[listingType]);

  const listingImageTitle = T(
    'listings_page.listing_card.image_title',
    listingTypeTranslation,
    totalRentalPrice,
    streetName,
    cityName,
  );

  const trackListingClick = React.useCallback(() => {
    const listingClickPayload = {
      citysearch: cityName,
      affiliate: 'none',
    };

    track(AnalyticsEvent.SearchListingClicked, listingClickPayload);
  }, [cityName]);

  const handleFavoriteClick = React.useCallback(async () => {
    try {
      if (!isAuthenticated) {
        navigateToAuthentication({
          action: AuthAction.Signup,
          source: AuthSource.ListingFavouriting,
        });

        return;
      }

      await apiClient.favorite.addFavoriteListing({
        id: listingId,
      });
    } catch (error) {
      reportError(new Error('Failed to favorite listing', { cause: error }), {
        extra: {
          listingId,
        },
      });
    }
  }, [
    isAuthenticated,
    navigateToAuthentication,
    apiClient.favorite,
    listingId,
  ]);

  // @todo Add isStillAvailable to Listing object
  const isStillAvailable = false;
  const listingLabelOrderPreference = [
    { key: ListingChipLabels.New, value: isNewAdvert },
    { key: ListingChipLabels.ReactForFree, value: isReactForFree },
    { key: ListingChipLabels.TopAd, value: isTopAdvert },
    { key: ListingChipLabels.StillAvailable, value: isStillAvailable },
  ];

  const activeLabels = listingLabelOrderPreference.filter(cond => cond.value);

  // Only show the first two most important labels
  const labelsToRender = activeLabels.slice(0, 2);

  const listingDetailsPageUrl = ROUTES.ListingDetails.getURL(
    {
      city: citySlug,
      street: streetSlug,
      listingID: listingId,
      listingType,
    },
    locale,
    'prefix',
  );

  return (
    <SearchResultCard href={listingDetailsPageUrl} onClick={trackListingClick}>
      <SearchResultCard.Header
        image={
          <SearchResultCard.Image
            alt={listingImageTitle}
            url={listingImageUrl}
            fallbackURL={NoListingImage.src}
            className={styles.listingImage}
            onError={handleListingImageError}
          />
        }
        labels={labelsToRender.map(labelToRender => (
          <ListingChip key={labelToRender.key} label={labelToRender.key} />
        ))}
        isFavorite={isFavorite}
        onFavorite={handleFavoriteClick}
      />
      <SearchResultCard.Content>
        <SearchResultCard.ContentRow>
          <Typography
            variant="subtitle1"
            component="span"
            className={CommonStyles.whiteSpaceNoWrap}
          >
            {streetName},
          </Typography>
          <Typography variant="subtitle1" component="span" noWrap>
            {cityName}
          </Typography>
        </SearchResultCard.ContentRow>

        <SearchResultCard.ContentRow>
          <Typography
            variant="body2"
            className={CommonStyles.whiteSpaceNoWrap}
          >{`${surfaceArea} m²`}</Typography>
          <Typography variant="body2">
            {T(FurnishingLabelTranslations[furnishingId]).toLocaleLowerCase()}
          </Typography>
          <Typography variant="body2" noWrap>
            {T(RoomTypeIDTranslations[listingType]) ?? listingType}
          </Typography>
          {isStudentHouseAdvert && (
            <Typography
              variant="body2"
              className={CommonStyles.textEllipsis}
              noWrap
            >
              {T('advert.in_studenthouse')}
            </Typography>
          )}
        </SearchResultCard.ContentRow>
        <SearchResultCard.ContentRow>
          <Typography variant="body2">{availability}</Typography>
        </SearchResultCard.ContentRow>
        <SearchResultCard.ContentRow>
          <Typography variant="h5" component="span">
            {formatCurrency(
              totalRentalPrice * 100,
              'EUR',
              CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION,
            )}
          </Typography>

          <Typography variant="body2">
            {utilitiesIncluded
              ? T('label.per_month.incl_utilities')
              : T('label.per_month')}
          </Typography>
        </SearchResultCard.ContentRow>
      </SearchResultCard.Content>
    </SearchResultCard>
  );
};
export const ListingCard = React.memo(ListingCardInternal);
